// src/lib/rtc.js — PeerManager (mediasoup SFU) compatibel met jouw App.js
import { socket } from "./sessionApi";
import * as mediasoupClient from "mediasoup-client";

class PeerManager {
  constructor({ getLocalStream, onPeerStream, onPeerRemove, iceServers }) {
    this.getLocalStream = getLocalStream || (() => null);
    this.onPeerStream = onPeerStream || (() => {});
    this.onPeerRemove = onPeerRemove || (() => {});
    this.iceServers = iceServers || [];

    this.device = null;
    this.sendT = null;
    this.recvT = null;

    this.localStream = null;
    this.producers = new Map(); // kind -> producer
    this.consumers = new Map(); // producerId -> { consumer, peerId, kind }

    // samengestelde streams per peer (audio+video samen)
    this.peerStreams = new Map(); // peerId -> MediaStream
    this.prodToPeer = new Map();  // producerId -> peerId
  }

  wireSocketHandlers() {
    socket.on("rtc:new-producer", async ({ peerId, producerId }) => {
      this.prodToPeer.set(producerId, peerId);
      try { await this._consume(producerId); } catch {}
    });

    // compat cleanup bij peer leave (simple-peer pad)
    socket.on("webrtc:peer-left", ({ id }) => {
      this._removePeer(id);
      this.onPeerRemove?.(id);
    });
  }

  async joinRoom() {
    const joinRes = await new Promise((res) =>
      socket.emit("rtc:join", {}, (r) => res(r))
    );
    if (!joinRes?.ok) throw new Error(joinRes?.error || "rtc:join failed");

    this.device = new mediasoupClient.Device();
    await this.device.load({ routerRtpCapabilities: joinRes.rtpCapabilities });

    await this._createTransports();

    const ls = this.getLocalStream?.();
    if (ls) await this.setLocalStream(ls);

    const list = Array.isArray(joinRes.producers) ? joinRes.producers : [];
    for (const { producerId, peerId } of list) {
      this.prodToPeer.set(producerId, peerId);
      try { await this._consume(producerId); } catch {}
    }
  }

  _removePeer(peerId) {
    // verwijder alle consumers + tracks uit samengestelde stream
    for (const [prodId, info] of [...this.consumers.entries()]) {
      if (info.peerId === peerId) {
        try { info.consumer.close(); } catch {}
        this.consumers.delete(prodId);
      }
    }
    const ms = this.peerStreams.get(peerId);
    if (ms) {
      for (const t of ms.getTracks()) { try { ms.removeTrack(t); } catch {} }
      this.peerStreams.delete(peerId);
    }
  }

  async _createTransports() {
    // SEND
    const sendP = await new Promise((res) =>
      socket.emit("rtc:createTransport", { direction: "send" }, (r) => res(r))
    );
    if (sendP?.error) throw new Error(sendP.error);
    this.sendT = this.device.createSendTransport(sendP);
    this.sendT.on("connect", ({ dtlsParameters }, cb, errCb) => {
      socket.emit("rtc:connectTransport", { transportId: this.sendT.id, dtlsParameters }, (r) =>
        r?.ok ? cb() : errCb(r?.error || "connect send failed")
      );
    });
    this.sendT.on("produce", ({ kind, rtpParameters, appData }, cb, errCb) => {
      socket.emit("rtc:produce",
        { transportId: this.sendT.id, kind, rtpParameters, appData },
        ({ id, error }) => (error ? errCb(error) : cb({ id }))
      );
    });

    // RECV
    const recvP = await new Promise((res) =>
      socket.emit("rtc:createTransport", { direction: "recv" }, (r) => res(r))
    );
    if (recvP?.error) throw new Error(recvP.error);
    this.recvT = this.device.createRecvTransport(recvP);
    this.recvT.on("connect", ({ dtlsParameters }, cb, errCb) => {
      socket.emit("rtc:connectTransport", { transportId: this.recvT.id, dtlsParameters }, (r) =>
        r?.ok ? cb() : errCb(r?.error || "connect recv failed")
      );
    });
  }

  // robuuste versie (fix “track ended”)
  async setLocalStream(stream) {
    const isLive = (t) => t && t.readyState === "live";
    let audio = (stream && stream.getAudioTracks().find(isLive)) || null;
    let video = (stream && stream.getVideoTracks().find(isLive)) || null;

    if (!audio && !video) {
  try {
    const constraints = {
      audio: {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
      },
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30, max: 30 },
      },
    };

    stream = await navigator.mediaDevices.getUserMedia(constraints);
    audio = stream.getAudioTracks().find(isLive) || null;
    video = stream.getVideoTracks().find(isLive) || null;
  } catch (e) {
    console.warn("[PeerManager] getUserMedia fallback faalde:", e);
  }
}


    this.localStream = stream;

    // oude producers sluiten
    for (const p of this.producers.values()) { try { p.close(); } catch {} }
    this.producers.clear();

    if (!this.sendT) return;

    if (audio) {
      try {
        const pa = await this.sendT.produce({ track: audio });
        this.producers.set("audio", pa);
        audio.addEventListener("ended", () => {
          try { pa.close(); } catch {}
          this.producers.delete("audio");
        });
      } catch (e) { console.warn("[PeerManager] produce audio mislukte:", e); }
    }

   if (video) {
  try {
    const pv = await this.sendT.produce({
      track: video,
      encodings: [
        {
          maxBitrate: 250_000,          // lage kwaliteit / fallback
          scaleResolutionDownBy: 2.5,   // bv. ~512x288 als input 1280x720 is
        },
        {
          maxBitrate: 900_000,          // normale kwaliteit (meestal gekozen)
          scaleResolutionDownBy: 1.5,   // bv. ~854x480
        },
        {
          maxBitrate: 2_500_000,        // hoge kwaliteit (goede verbinding + grote tile)
          scaleResolutionDownBy: 1.0,   // volledige 720p
        },
      ],
      codecOptions: { videoGoogleStartBitrate: 1200 },
    });

        this.producers.set("video", pv);
        video.addEventListener("ended", () => {
          try { pv.close(); } catch {}
          this.producers.delete("video");
        });
      } catch (e) { console.warn("[PeerManager] produce video mislukte:", e); }
    }
  }

  async addLocalTracksToAll() {
    if (this.localStream) await this.setLocalStream(this.localStream);
  }
    setMicEnabled(enabled) {
    // audio-producer track uit/aan
    for (const [kind, producer] of this.producers.entries()) {
      if (kind !== "audio") continue;
      const track = producer.track;
      if (track) {
        track.enabled = !!enabled;
      }
    }

    // lokale stream synchroon houden
    const ls = this.localStream;
    if (ls) {
      ls.getAudioTracks().forEach((t) => {
        t.enabled = !!enabled;
      });
    }
  }


  // NIEUW: consume met “nieuwe streamreferentie” bij elke update (voorkomt freeze; audio+video samen)
  async _consume(producerId) {
    if (!this.recvT || !this.device) return;

    const params = await new Promise((res) =>
      socket.emit(
        "rtc:consume",
        { transportId: this.recvT.id, producerId, rtpCapabilities: this.device.rtpCapabilities },
        (r) => res(r)
      )
    );
    if (!params || params.error) return;

    const consumer = await this.recvT.consume({
      id: params.id,
      producerId: params.producerId,
      kind: params.kind,
      rtpParameters: params.rtpParameters,
    });

    let peerId =
      params.peerId ||
      this.prodToPeer.get(producerId) ||
      null;

    // fallback: gebruik een unieke ID zodat meerdere producers niet onder één "peer" vallen
    if (!peerId) {
      peerId = `peer-${producerId}`;
    }
    this.consumers.set(producerId, { consumer, peerId, kind: params.kind });

    // maak elke update een NIEUWE MediaStream met alle tracks (triggert video.play())
    const existing = this.peerStreams.get(peerId) || new MediaStream();
    const tracks = [
      ...existing.getTracks().filter((t) => t.kind !== consumer.kind),
      consumer.track,
    ];
    const merged = new MediaStream(tracks);
    this.peerStreams.set(peerId, merged);

    // cleanup hooks
    const removeFromMap = () => {
      const cur = this.peerStreams.get(peerId);
      if (!cur) return;
      const left = cur.getTracks().filter((t) => t.id !== consumer.track.id);
      const next = new MediaStream(left);
      if (left.length) this.peerStreams.set(peerId, next);
      else this.peerStreams.delete(peerId);
      this.onPeerStream?.(peerId, next); // push update naar UI
    };
    consumer.track.addEventListener?.("ended", removeFromMap);
    consumer.on("transportclose", () => {
      try { consumer.close(); } catch {}
      removeFromMap();
    });

    // eerst resume, dan doorgeven — minder kans op “stil” blijven
    try {
      await new Promise((res) => socket.emit("rtc:resume", { consumerId: consumer.id }, () => res(true)));
    } catch {}

    // push update naar UI (nieuwe referentie triggert <video>.play())
    this.onPeerStream?.(peerId, merged);
  }

  destroy() {
    try { for (const { consumer } of this.consumers.values()) consumer.close(); } catch {}
    try { for (const p of this.producers.values()) p.close(); } catch {}
    try { this.sendT?.close(); } catch {}
    try { this.recvT?.close(); } catch {}
    this.consumers.clear();
    this.producers.clear();
    this.peerStreams.clear();
  }
}

export default PeerManager;
export { PeerManager };
